/**
 * Advanced-search: discover which object template the current filter is scoped to.
 *
 * Custom-object ATTRIBUTE filtering (`value.<attr>`) only makes sense once the search
 * is narrowed to a single object template — the template's attribute schema is what
 * turns into the synthetic `value.<attr>` filter fields (see objectTemplateFilterFields.ts).
 *
 * Per the chosen UX (operator-question-fe-advanced-search-attr-filter-selection-ux),
 * selecting an object template "sets/maintains the object_template_id filter". So the
 * single source of truth for *which* template is active is the filter itself: an
 * `object_template_id eq <id>` leaf. This holds for every entry path that scopes to a
 * template — the CustomObjectList "Advanced search" deep-link
 * (buildRecordListAdvancedSearchPath('custom_object', {and:[{field:'object_template_id',
 * op:'eq', value:<id>}]})), the filter-builder's "object template" FK field option, and
 * (later) the type-dropdown object-template submenu — all express the choice the same way.
 *
 * This module reads that leaf out of the (live or committed) advanced-search filter
 * values. It is intentionally pure (no React, shape-agnostic) so it can be unit-tested
 * without a browser. See selectedObjectTemplate.test.ts.
 */

/**
 * Field names whose `eq` value is an object_template id. `object_template_id` is the
 * real custom_object FK column (used by the deep-link and the "object template" FK
 * option); `object_template_id--object_template.id` is the extended "object template
 * (all attributes)" FK id path. Both unambiguously carry an object template id.
 */
const OBJECT_TEMPLATE_ID_FIELDS = new Set([
    "object_template_id",
    "object_template_id--object_template.id",
]);

/** Depth guard — filter trees are tiny; this only bounds pathological input. */
const MAX_DEPTH = 30;

const isMatchingLeaf = (node: Record<string, unknown>): boolean =>
    typeof node.field === "string" &&
    OBJECT_TEMPLATE_ID_FIELDS.has(node.field) &&
    node.op === "eq" &&
    typeof node.value === "string" &&
    node.value.length > 0;

const collect = (node: unknown, found: Set<string>, depth: number): void => {
    if (node == null || depth > MAX_DEPTH) {
        return;
    }
    if (Array.isArray(node)) {
        node.forEach((child) => collect(child, found, depth + 1));
        return;
    }
    if (typeof node !== "object") {
        return;
    }
    const record = node as Record<string, unknown>;
    if (isMatchingLeaf(record)) {
        found.add(record.value as string);
    }
    Object.values(record).forEach((child) => collect(child, found, depth + 1));
};

/**
 * Find the object template id the given advanced-search filter values are scoped to.
 *
 * Returns the id when the filter pins exactly one object template
 * (`object_template_id eq <id>`); returns undefined when there is none, or when several
 * *different* ids are present (ambiguous — a custom object has exactly one template, so
 * conflicting pins can't narrow to a single attribute schema; don't guess).
 *
 * @param values - the advanced-search filter form values (a nested and/or tree; extra
 *   sibling keys such as `resource_type` are ignored) or a committed CustomFilterValues.
 */
export const findFilteredObjectTemplateId = (values: unknown): string | undefined => {
    const found = new Set<string>();
    collect(values, found, 0);
    return found.size === 1 ? [...found][0] : undefined;
};
